/**
 *
 * "The Dashboard page. First page viewed by a logged in User."
 *
 * @file   DashboardPage.js
 * @author Lateral
 * @since  2023
 */

import React, { useContext } from 'react'
import { Box, Stack, Typography, Button, styled, Grid } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import useMediaQuery from '@mui/material/useMediaQuery'
import { DESKTOP_MIN_WIDTH } from 'common/config'
import { observeDialogue, useCurrentUser } from 'hooks'
import RecentActivityTable from 'components/tables/history/RecentActivityTable'
import CondensedDialogueTable from 'components/tables/dialogue/CondensedDialogueTable'
import { CurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'
import { Header } from 'components'

const DashboardButton = styled(Button)(({ theme }) => {
  /**
   *  Dashboard button styling
   *
   * @function
   * @returns {object} - Button style
   */
  return {
    color: theme.palette.secondary.main,
    background: '#242424',
    width: '100%',
    height: '96px',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: '2px'
  }
})

function DashboardPage() {
  /**
   *  Create dashboard page layout
   *
   * @function
   * @returns {object} - Dashboard page
   */
  const { database } = useContext(CurrentDeckContext)
  const dialogue = observeDialogue()
  const isDesktop = useMediaQuery(DESKTOP_MIN_WIDTH)
  const { currentUser } = useCurrentUser()
  return (
    <>
      <Header>
        <Typography variant="h4" component="h3">
          {`Welcome back, ${currentUser.attributes['given_name']}`}
        </Typography>
      </Header>
      <Box
        className="dashboard"
        mt={2}
        sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'nowrap', height: '100%', px: '15px', pb: '15px' }}>
        <Box mt={3} height="100%">
          {isDesktop ? (
            <Grid
              gap={2}
              item
              container
              flexDirection="row"
              justifyContent="space-between"
              mt={4}
              height="100%"
              flexWrap="nowrap">
              <Grid overflow="hidden" sm={6} item>
                <RecentActivityTable
                  decks={database.decks}
                  deckRevisions={database.deckRevisions}
                  deckRevisionHistories={database.deckRevisionHistories}
                  sites={database.sites}
                  locations={database.locations}
                  isMobile={false}
                />
              </Grid>
              <Grid overflow="hidden" sm={6} item>
                <CondensedDialogueTable dialogue={dialogue} isMobile={false} />
              </Grid>
            </Grid>
          ) : (
            <Stack spacing={2} direction="column" mt="2em">
              <DashboardButton component={RouterLink} to="/mobile/decklayout">
                Deck Layout
              </DashboardButton>
              <DashboardButton component={RouterLink} to="/mobile/maintenance">
                Maintenance
              </DashboardButton>
            </Stack>
          )}
        </Box>
      </Box>
    </>
  )
}
export default DashboardPage
